/**
 * ShareholderStructureTab - Orchestrator Component
 *
 * Composes the Shareholders tab from state owned by CompanyDetails (useShareholderState):
 * - Filter button, the removable applied-filter chips and ShareholderFilters (mini variant)
 * - Desktop: a full-width 'Shareholders structure' card with a Graph | Table switch in its
 *   heading. Graph view: the legend and the card graph, with the Ultimate shareholders and
 *   Subsidiaries tables side by side below it at a fixed height; pointing at a row of either
 *   table highlights its card in the graph, and a row whose card the graph does not draw
 *   offers 'Show in graph'. Table view: the levels table
 *   (ShareholderLevelsTable) in place of the legend, the graph and both tables.
 * - Mobile: the same heading with the compact ShareholderMobileList, then the Subsidiaries table
 *   (no switch)
 *
 * @module ShareholderStructureTab
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../../../buttons/IconButton';
import ShareholderFilters from './ShareholderFilters';
import ShareholderFilterChips from './ShareholderFilterChips';
import ShareholderGraph from './ShareholderGraph';
import ShareholderLegend from './ShareholderLegend';
import ShareholderLevelsTable from './ShareholderLevelsTable';
import ShareholderMobileList from './ShareholderMobileList';
import SubsidiariesTable from './SubsidiariesTable';
import UltimateShareholdersTable from './UltimateShareholdersTable';
import { GRAPH_COPY, SHAREHOLDER_TABLE_HEIGHT, STRUCTURE_VIEWS } from './graph/graphConstants';
import { nodeIdOf } from './graph/prepareShareholderGraph';
import { FIELD_SOURCE, dataSourceClass } from '../../../../utils/constants/dataSources';

// Target of the mini graph's 'Skip graph' link.
export const SHAREHOLDER_TABLES_ID = 'shareholder-structure-tables';

const DEFAULT_APPLIED_FILTERS = {
  percentageFilter: 5,
  includeUnknownShares: true,
  includeSubsidiaries: false,
  expandGeneralPartners: false
};

const VIEW_OPTIONS = [
  { value: STRUCTURE_VIEWS.GRAPH, label: GRAPH_COPY.VIEW_GRAPH },
  { value: STRUCTURE_VIEWS.TABLE, label: GRAPH_COPY.VIEW_TABLE }
];

// The control that opens or closes the mini filter panel can remove itself ('Change filter', the
// panel's own buttons), which drops the focus on the page. It goes to the Filter button instead;
// the next Tab enters the panel, which follows the button in the DOM. Focus that a control still
// holds stays put, e.g. on the next chip after a chip removal closed the panel.
const useFilterPanelFocus = (showFilters, filterButtonId) => {
  const wasShownRef = useRef(showFilters);
  useEffect(() => {
    if (wasShownRef.current === showFilters) return;
    wasShownRef.current = showFilters;
    const active = document.activeElement;
    if (active && active !== document.body) return;
    document.getElementById(filterButtonId)?.focus({ preventScroll: true });
  }, [showFilters, filterButtonId]);
};

// The Ultimate shareholder(s) or Subsidiaries row under the pointer, for the mini graph above the
// tables. A row can go without a mouseleave (Table view, phone layout, new rows after a filter),
// so the value belongs to one graph and resets when the tables stop sitting below the graph.
const useHoveredTableRow = (graph, isLinked) => {
  const [hovered, setHovered] = useState(null);
  const [wasLinked, setWasLinked] = useState(isLinked);
  if (wasLinked !== isLinked) {
    setWasLinked(isLinked);
    setHovered(null);
  }
  const onHover = useCallback(
    (entry) => setHovered(entry ? { id: nodeIdOf(entry), graph } : null),
    [graph]
  );
  return [isLinked && hovered?.graph === graph ? hovered.id : null, onHover];
};

// A table row lights up its card only when the graph draws one (an owner behind a collapsed
// card, or a subsidiary while the Subsidiaries filter excludes them, has none). 'Show in graph'
// on such a row runs the action that brings the card back; the graph then focuses the card,
// which highlights it, and the structure card scrolls into view.
const useShowInGraph = (graph, structureCardRef) => {
  const drawnIds = useMemo(() => new Set((graph?.nodes ?? []).map((node) => node.id)), [graph]);
  const isInGraph = useCallback((entry) => drawnIds.has(nodeIdOf(entry)), [drawnIds]);
  const [focusRequest, setFocusRequest] = useState(null);
  const showWith = useCallback(
    (reveal) =>
      reveal &&
      ((entry) => {
        setFocusRequest({ nodeId: nodeIdOf(entry) });
        structureCardRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
        reveal();
      }),
    [structureCardRef]
  );
  return { isInGraph, focusRequest, showWith };
};

// The Graph | Table pills in the card heading, the same pills as the phone list's toggle.
const StructureViewToggle = ({ view, onChange }) => (
  <fieldset className="sg-view-toggle" aria-label={GRAPH_COPY.VIEW_SWITCH}>
    {VIEW_OPTIONS.map(({ value, label }) => (
      <button
        key={value}
        type="button"
        className="sg-view-toggle-button"
        aria-pressed={view === value}
        onClick={() => {
          if (view !== value) onChange(value);
        }}
      >
        {label}
      </button>
    ))}
  </fieldset>
);

StructureViewToggle.propTypes = {
  view: PropTypes.oneOf(Object.values(STRUCTURE_VIEWS)).isRequired,
  onChange: PropTypes.func.isRequired
};

// The card body: the phone list, the levels table (desktop Table view) or the legend with the
// mini graph (desktop Graph view).
const StructureCardBody = ({
  isMobile,
  showTable,
  headingId,
  graph,
  figuresStatus,
  percentageFilter,
  isIndependent,
  companyName,
  getNodeHref,
  onViewNodeDetails,
  onOpenNodeInNewTab,
  onToggleNodeCollapsed,
  onShowAllFiltered,
  onExpandAllOwners,
  onOpenFilters,
  onTogglePopup,
  focusRequest,
  isLayoutLocked,
  onToggleLayoutLock,
  canExport,
  isExportDisabled,
  onExportError,
  highlightedNodeId,
  onConfirmDownload
}) => {
  if (isMobile) {
    return (
      <ShareholderMobileList
        graph={graph}
        figuresStatus={figuresStatus}
        percentageFilter={percentageFilter}
        isIndependent={isIndependent}
        companyName={companyName}
        onViewNodeDetails={onViewNodeDetails}
        onToggleNodeCollapsed={onToggleNodeCollapsed}
        onShowAllFiltered={onShowAllFiltered}
        onExpandAllOwners={onExpandAllOwners}
        onTogglePopup={onTogglePopup}
      />
    );
  }
  if (showTable) {
    return (
      <ShareholderLevelsTable
        graph={graph}
        figuresStatus={figuresStatus}
        percentageFilter={percentageFilter}
        labelledBy={headingId}
        isIndependent={isIndependent}
        companyName={companyName}
        getNodeHref={getNodeHref}
        onViewNodeDetails={onViewNodeDetails}
        onOpenNodeInNewTab={onOpenNodeInNewTab}
        onToggleNodeCollapsed={onToggleNodeCollapsed}
        onShowAllFiltered={onShowAllFiltered}
        onExpandAllOwners={onExpandAllOwners}
        onOpenFilters={onOpenFilters}
      />
    );
  }
  return (
    <>
      <ShareholderLegend />
      <ShareholderGraph
        graph={graph}
        variant="mini"
        companyName={companyName}
        figuresStatus={figuresStatus}
        percentageFilter={percentageFilter}
        getNodeHref={getNodeHref}
        onViewNodeDetails={onViewNodeDetails}
        onOpenNodeInNewTab={onOpenNodeInNewTab}
        onToggleFullScreen={onTogglePopup}
        focusRequest={focusRequest}
        onShowAllFiltered={onShowAllFiltered}
        onToggleNodeCollapsed={onToggleNodeCollapsed}
        onOpenFilters={onOpenFilters}
        isLayoutLocked={isLayoutLocked}
        onToggleLayoutLock={onToggleLayoutLock}
        canExport={canExport}
        isExportDisabled={isExportDisabled}
        onExportError={onExportError}
        skipTargetId={SHAREHOLDER_TABLES_ID}
        highlightedNodeId={highlightedNodeId}
        onConfirmDownload={onConfirmDownload}
      />
    </>
  );
};

StructureCardBody.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  showTable: PropTypes.bool.isRequired,
  headingId: PropTypes.string.isRequired,
  graph: PropTypes.object,
  figuresStatus: PropTypes.oneOf(['idle', 'loading', 'loaded', 'failed']),
  percentageFilter: PropTypes.number,
  isIndependent: PropTypes.bool,
  companyName: PropTypes.string,
  getNodeHref: PropTypes.func,
  onViewNodeDetails: PropTypes.func,
  onOpenNodeInNewTab: PropTypes.func,
  onToggleNodeCollapsed: PropTypes.func,
  onShowAllFiltered: PropTypes.func,
  onExpandAllOwners: PropTypes.func,
  onOpenFilters: PropTypes.func,
  onTogglePopup: PropTypes.func,
  focusRequest: PropTypes.shape({ nodeId: PropTypes.string }),
  isLayoutLocked: PropTypes.bool,
  onToggleLayoutLock: PropTypes.func,
  canExport: PropTypes.bool,
  isExportDisabled: PropTypes.bool,
  onExportError: PropTypes.func,
  highlightedNodeId: PropTypes.string,
  onConfirmDownload: PropTypes.func
};

// Ultimate shareholders and Subsidiaries side by side at a fixed height below the graph on
// desktop. Phones get Subsidiaries only: the phone list above already shows the ultimate owners,
// with the table's notes.
const ShareholderTables = ({
  shareholders,
  subsidiaries,
  isIndependent,
  companyName,
  layout,
  isPhoneOrTablet,
  onViewShareholderDetails,
  onViewSubsidiaryDetails,
  onOpenNodeInNewTab,
  onHoverShareholder,
  onHoverSubsidiary,
  isInGraph,
  onShowShareholderInGraph,
  onShowSubsidiaryInGraph
}) => {
  /**
   * TD-1825: Combined handler for ultimate shareholders that routes to appropriate handler
   * - Persons: use onViewShareholderDetails (person profile lookup)
   * - Companies and subsidiaries: use onViewSubsidiaryDetails (company profile lookup), the
   *   types the shared navigability gate allows
   */
  const handleViewUltimateShareholderDetails = useCallback(
    (entry) => {
      if (entry?.type === 'person') {
        onViewShareholderDetails?.(entry);
      } else if (entry?.type === 'company' || entry?.type === 'subsidiary') {
        onViewSubsidiaryDetails?.(entry);
      }
    },
    [onViewShareholderDetails, onViewSubsidiaryDetails]
  );

  const isMobile = layout === 'mobile';
  const tableHeight = isMobile ? undefined : SHAREHOLDER_TABLE_HEIGHT;

  return (
    // tabIndex -1: the graph's 'Skip graph' link moves the focus here.
    <div className="left-lists-wrap" id={SHAREHOLDER_TABLES_ID} tabIndex={-1}>
      {!isMobile && (
        <UltimateShareholdersTable
          shareholders={shareholders}
          isIndependent={isIndependent}
          companyName={companyName}
          cardHeight={tableHeight}
          layout={layout}
          isPhoneOrTablet={isPhoneOrTablet}
          onViewDetails={handleViewUltimateShareholderDetails}
          // Table rows are the graph's own (raw) nodes, so the graph's new-tab handler serves
          // them too; the tables pass the row only, without a prepared node.
          onOpenDetailsInNewTab={onOpenNodeInNewTab}
          onHoverShareholder={onHoverShareholder}
          isInGraph={isInGraph}
          onShowInGraph={onShowShareholderInGraph}
        />
      )}
      <SubsidiariesTable
        subsidiaries={subsidiaries}
        cardHeight={tableHeight}
        layout={layout}
        isPhoneOrTablet={isPhoneOrTablet}
        onViewDetails={onViewSubsidiaryDetails}
        onOpenDetailsInNewTab={onOpenNodeInNewTab}
        onHoverSubsidiary={onHoverSubsidiary}
        isInGraph={isInGraph}
        onShowInGraph={onShowSubsidiaryInGraph}
      />
    </div>
  );
};

ShareholderTables.propTypes = {
  shareholders: PropTypes.array,
  subsidiaries: PropTypes.array,
  isIndependent: PropTypes.bool,
  companyName: PropTypes.string,
  layout: PropTypes.string,
  isPhoneOrTablet: PropTypes.bool,
  onViewShareholderDetails: PropTypes.func,
  onViewSubsidiaryDetails: PropTypes.func,
  onOpenNodeInNewTab: PropTypes.func,
  onHoverShareholder: PropTypes.func,
  onHoverSubsidiary: PropTypes.func,
  isInGraph: PropTypes.func,
  onShowShareholderInGraph: PropTypes.func,
  onShowSubsidiaryInGraph: PropTypes.func
};

/**
 * @param {Object} props
 * @param {Object|null} props.graph - PreparedGraph (useShareholderState.graph)
 * @param {'idle'|'loading'|'loaded'|'failed'} [props.figuresStatus] - card figure state
 * @param {Array} props.shareholders - Ultimate shareholders (of the filtered graph)
 * @param {Array} props.subsidiaries - Subsidiaries table rows
 * @param {boolean} props.isIndependent - Whether company has no majority shareholder
 * @param {string} props.companyName - Company name for messaging and the PNG file name
 * @param {string} props.layout - 'desktop' or 'mobile'
 * @param {boolean} props.isPhoneOrTablet - Device type flag
 * @param {'graph'|'table'} [props.structureView] - Desktop view: the graph with the two tables,
 *   or the levels table
 * @param {Function} [props.onStructureViewChange] - (view) => the Graph | Table switch; the
 *   switch shows only on desktop and only with this handler
 * @param {Object} props.filters - Pending filter form values
 * @param {Object} [props.appliedFilters] - Applied filters ({percentageFilter,
 *   includeUnknownShares, includeSubsidiaries, expandGeneralPartners}) for the chips and the
 *   no-owners message
 * @param {Function} props.onRemoveFilters - (filterKeys) => a chip's X lifts those filters
 * @param {Object} props.validationMessages - Validation error messages
 * @param {boolean} props.showOnSubmitErrorState - Show validation errors on submit
 * @param {boolean} props.isFilterDisabled - Whether the apply button is disabled
 * @param {boolean} props.isClearDisabled - Whether the clear button is disabled
 * @param {boolean} props.showFilters - Whether the filter panel is visible
 * @param {Function} props.onFilterChange - Filter value change
 * @param {Function} props.onApply - Apply filters
 * @param {Function} props.onClear - Clear filters
 * @param {Function} props.onToggleFilters - Toggle the filter panel
 * @param {Function} props.onTogglePopup - Open the full-screen popup
 * @param {Function} [props.getNodeHref] - (raw, node) => href of a graph node's page
 * @param {Function} [props.onViewNodeDetails] - (raw, node) => same-tab navigation
 * @param {Function} [props.onOpenNodeInNewTab] - (raw, node) => open in a new tab; the Ultimate
 *   shareholder(s) and Subsidiaries tables call it with their row only, and offer no 'Open in
 *   new tab' without it
 * @param {Function} [props.onToggleNodeCollapsed] - (nodeId) => collapse/expand owners
 * @param {Function} [props.onShowAllFiltered] - 'Show all' on the aggregate
 * @param {Function} [props.onExpandAllOwners] - 'Show all owners' below the levels table
 * @param {boolean} [props.isLayoutLocked] - Lock layout pressed: cards cannot be dragged
 * @param {Function} [props.onToggleLayoutLock] - the toolbar's Lock layout toggle
 * @param {boolean} [props.canExport] - false disables Download
 * @param {boolean} [props.isExportDisabled] - the profilerexportdisabled right (tooltip text)
 * @param {Function} [props.onExportError] - (message) => show a failed PNG export
 * @param {Function} [props.onConfirmDownload] - () => Promise<DOWNLOAD_VERDICT>: meters the
 *   PNG download once the image exists; only a recorded download is saved
 * @param {Function} props.onViewShareholderDetails - Tables: view a person shareholder
 * @param {Function} props.onViewSubsidiaryDetails - Tables: view a company
 */
const ShareholderStructureTab = observer(function ShareholderStructureTab({
  graph = null,
  figuresStatus = 'loaded',
  shareholders = [],
  subsidiaries = [],
  isIndependent = false,
  companyName = '',
  layout = 'desktop',
  isPhoneOrTablet = false,
  structureView = STRUCTURE_VIEWS.GRAPH,
  onStructureViewChange,
  filters = {},
  appliedFilters = DEFAULT_APPLIED_FILTERS,
  validationMessages = {},
  showOnSubmitErrorState = false,
  isFilterDisabled = false,
  isClearDisabled = false,
  showFilters = false,
  onFilterChange,
  onApply,
  onClear,
  onRemoveFilters,
  onToggleFilters,
  onTogglePopup,
  getNodeHref,
  onViewNodeDetails,
  onOpenNodeInNewTab,
  onToggleNodeCollapsed,
  onShowAllFiltered,
  onExpandAllOwners,
  isLayoutLocked,
  onToggleLayoutLock,
  canExport = true,
  isExportDisabled = false,
  onExportError,
  onConfirmDownload,
  onViewShareholderDetails,
  onViewSubsidiaryDetails
}) {
  const isMobile = layout === 'mobile';
  const showTable = !isMobile && structureView === STRUCTURE_VIEWS.TABLE;
  // Desktop Graph view: the tables sit below the graph.
  const linksTableToGraph = !isMobile && !showTable;
  const [highlightedNodeId, handleHoverRow] = useHoveredTableRow(graph, linksTableToGraph);
  const structureCardRef = useRef(null);
  const { isInGraph, focusRequest, showWith } = useShowInGraph(graph, structureCardRef);
  // What brings a hidden card back: an owner only hides behind a collapsed card (the list
  // leaves the owners the filters hide in the aggregate), a subsidiary only while the
  // Subsidiaries filter excludes them.
  const showShareholderInGraph =
    linksTableToGraph && graph?.collapsedNodeIds?.length ? showWith(onExpandAllOwners) : undefined;
  const showSubsidiaryInGraph =
    linksTableToGraph && !appliedFilters.includeSubsidiaries
      ? showWith(() => onRemoveFilters(['includeSubsidiaries']))
      : undefined;
  const filterButtonId = `sg-filter-button-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const headingId = `sg-structure-heading-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  useFilterPanelFocus(showFilters, filterButtonId);

  return (
    <div className={`shareholder-structure-tab-cards ${isMobile ? 'is-mobile' : 'is-desktop'}`}>
      {/* Filter Button, Panel and applied-filter chips. The panel is placed absolutely below the
          row; it comes right after the button so that Tab reaches it before the chips. */}
      <div className="card structure-filter-wrap">
        <IconButton
          accessible
          id={filterButtonId}
          icon={ICON_BUTTON_ICONS.FILTER}
          hoverType={ICON_BUTTON_TYPES.BLUE}
          innerText="Filter"
          onClick={onToggleFilters}
          filled={showFilters}
          disabled={showFilters}
        />
        {showFilters && (
          <ShareholderFilters
            variant="mini"
            filters={filters}
            validationMessages={validationMessages}
            showOnSubmitErrorState={showOnSubmitErrorState}
            isFilterDisabled={isFilterDisabled}
            isClearDisabled={isClearDisabled}
            onFilterChange={onFilterChange}
            onApply={onApply}
            onClear={onClear}
            onClose={onToggleFilters}
          />
        )}
        <ShareholderFilterChips
          filters={appliedFilters}
          context={{ collapsedGeneralPartnerCount: graph?.collapsedGeneralPartnerCount ?? 0 }}
          onRemoveFilters={onRemoveFilters}
          fallbackFocusId={filterButtonId}
        />
      </div>

      <div className="card shareholders-structure-card" ref={structureCardRef}>
        <div className="sg-structure-heading-row">
          <div id={headingId} className={`heading ${dataSourceClass(FIELD_SOURCE.shareholders)}`}>
            Shareholders structure
          </div>
          {!isMobile && onStructureViewChange && (
            <StructureViewToggle view={structureView} onChange={onStructureViewChange} />
          )}
        </div>
        <div className="shareholders-structure-wrap">
          <StructureCardBody
            isMobile={isMobile}
            showTable={showTable}
            headingId={headingId}
            graph={graph}
            figuresStatus={figuresStatus}
            percentageFilter={appliedFilters.percentageFilter}
            isIndependent={isIndependent}
            companyName={companyName}
            getNodeHref={getNodeHref}
            onViewNodeDetails={onViewNodeDetails}
            onOpenNodeInNewTab={onOpenNodeInNewTab}
            onToggleNodeCollapsed={onToggleNodeCollapsed}
            onShowAllFiltered={onShowAllFiltered}
            onExpandAllOwners={onExpandAllOwners}
            onOpenFilters={showFilters ? undefined : onToggleFilters}
            onTogglePopup={onTogglePopup}
            focusRequest={focusRequest}
            isLayoutLocked={isLayoutLocked}
            onToggleLayoutLock={onToggleLayoutLock}
            canExport={canExport}
            isExportDisabled={isExportDisabled}
            onExportError={onExportError}
            onConfirmDownload={onConfirmDownload}
            highlightedNodeId={highlightedNodeId}
          />
        </div>
      </div>

      {!showTable && (
        <ShareholderTables
          shareholders={shareholders}
          subsidiaries={subsidiaries}
          isIndependent={isIndependent}
          companyName={companyName}
          layout={layout}
          isPhoneOrTablet={isPhoneOrTablet}
          onViewShareholderDetails={onViewShareholderDetails}
          onViewSubsidiaryDetails={onViewSubsidiaryDetails}
          onOpenNodeInNewTab={onOpenNodeInNewTab}
          onHoverShareholder={linksTableToGraph ? handleHoverRow : undefined}
          onHoverSubsidiary={linksTableToGraph ? handleHoverRow : undefined}
          isInGraph={isInGraph}
          onShowShareholderInGraph={showShareholderInGraph}
          onShowSubsidiaryInGraph={showSubsidiaryInGraph}
        />
      )}
    </div>
  );
});

ShareholderStructureTab.propTypes = {
  graph: PropTypes.object,
  figuresStatus: PropTypes.oneOf(['idle', 'loading', 'loaded', 'failed']),
  shareholders: PropTypes.array,
  subsidiaries: PropTypes.array,
  isIndependent: PropTypes.bool,
  companyName: PropTypes.string,
  layout: PropTypes.string,
  isPhoneOrTablet: PropTypes.bool,
  structureView: PropTypes.oneOf(Object.values(STRUCTURE_VIEWS)),
  onStructureViewChange: PropTypes.func,
  filters: PropTypes.object,
  appliedFilters: PropTypes.shape({
    percentageFilter: PropTypes.number,
    includeUnknownShares: PropTypes.bool,
    includeSubsidiaries: PropTypes.bool,
    expandGeneralPartners: PropTypes.bool
  }),
  validationMessages: PropTypes.object,
  showOnSubmitErrorState: PropTypes.bool,
  isFilterDisabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  isClearDisabled: PropTypes.bool,
  showFilters: PropTypes.bool,
  onFilterChange: PropTypes.func,
  onApply: PropTypes.func,
  onClear: PropTypes.func,
  onRemoveFilters: PropTypes.func.isRequired,
  onToggleFilters: PropTypes.func,
  onTogglePopup: PropTypes.func,
  getNodeHref: PropTypes.func,
  onViewNodeDetails: PropTypes.func,
  onOpenNodeInNewTab: PropTypes.func,
  onToggleNodeCollapsed: PropTypes.func,
  onShowAllFiltered: PropTypes.func,
  onExpandAllOwners: PropTypes.func,
  isLayoutLocked: PropTypes.bool,
  onToggleLayoutLock: PropTypes.func,
  canExport: PropTypes.bool,
  isExportDisabled: PropTypes.bool,
  onExportError: PropTypes.func,
  onConfirmDownload: PropTypes.func,
  onViewShareholderDetails: PropTypes.func,
  onViewSubsidiaryDetails: PropTypes.func
};

export default ShareholderStructureTab;
